import { ExclusionListSelectorView } from '../view/ExclusionListSelectorView';
import { templateExclusionListSelector } from '../view/template';
import { ExclusionListSelectorRepository } from '../repository/ExclusionListSelectorRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { ExclusionListSelectorWebComponent } from '..';
import { ExclusionListSelectorWebComponentSchema } from '../types';
import { ExclusionListItem, ExclusionListTypes } from '../../exclusionsPanel/types';
import { SelectOption } from '../../../ui/gzDropdown/types';
import { GzDropdownWebComponent } from '../../../ui/gzDropdown';

export class ExclusionListSelectorController extends BaseWebComponentController {
  public component: ExclusionListSelectorWebComponent;
  public dataRepository: ExclusionListSelectorRepository;
  public view: ExclusionListSelectorView;
  public viewConstructor: typeof ExclusionListSelectorView;
  public dataRepositoryConstructor: typeof ExclusionListSelectorRepository;

  private searchStringToExclusionLists: Map<string, Array<ExclusionListItem>>;

  private static getExclusionListSelectorOptions(
    exclusionLists: Array<ExclusionListItem> | undefined
  ): Array<SelectOption> {
    return exclusionLists
      ? exclusionLists.map(ExclusionList => {
          return {
            title: ExclusionList.label,
            value: ExclusionList.value,
          };
        })
      : [];
  }

  constructor(component: ExclusionListSelectorWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: ExclusionListSelectorView,
      viewTemplate: templateExclusionListSelector,
      dataRepositoryConstructor: ExclusionListSelectorRepository,
    });

    this.searchStringToExclusionLists = new Map<string, Array<ExclusionListItem>>();

    // This is the initial state of the selector, corresponding to empty search string. Put it in the cache.
    this.searchStringToExclusionLists.set('', component.exclusionList);
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$exclusionListSelectorDropdown,
        event: 'selectionChanged',
        callback: this.onSelectedExclusionListChanged,
      },
      {
        element: this.view.dom.$exclusionListSelectorDropdown,
        event: 'onNewInputString',
        callback: this.onNewInputString,
      },
    ];
  }

  async onSelectedExclusionListChanged(ev: WebComponentEvent) {
    const selectedOption = ev.detail;

    // Save the previous active ExclusionList, so we can reset it in the dropdown if we get an error.
    const previousTarget = this.component.selectedTarget;
    const currentTarget = {
      value: selectedOption.value,
      label: selectedOption.label,
    };

    if (currentTarget.value === this.component.selectedTarget.value) {
      return false;
    }

    try {
      this.component.selectedTarget = currentTarget;

      const ExclusionListClickedHandler = this.component.onExclusionListClicked;
      if (ExclusionListClickedHandler) {
        await ExclusionListClickedHandler(currentTarget);
      }

      const ExclusionListChangedWithSuccessHandler = this.component.onExclusionListChangedWithSuccess;
      if (ExclusionListChangedWithSuccessHandler) {
        await ExclusionListChangedWithSuccessHandler();
        return true;
      }
    } catch (error) {
      console.error(error);

      // Revert the previously selected ExclusionList.
      this.component.selectedTarget = previousTarget;
      const lastSelected = {
        title: previousTarget.label,
        value: previousTarget.value,
      };
      const selector = this.view.dom.$exclusionListSelectorDropdown;
      selector.selectedOption = lastSelected;
      selector.value = lastSelected.value;
      selector.setLastSelectedOption();

      const exclusionListChangedWithErrorHandler = this.component.onExclusionListChangedWithError;
      if (exclusionListChangedWithErrorHandler) {
        await exclusionListChangedWithErrorHandler(error);
      }

      // Show an error dialog.
      this.view.showErrorDialog(currentTarget.label);
    }

    return false;
  }

  async onNewInputString(ev: WebComponentEvent) {
    const selector = ev.target as GzDropdownWebComponent;
    selector.isError = false;
    selector.isLoading = true;

    try {
      const { searchString } = ev.detail;

      const ExclusionLists = await this.getExclusionLists(searchString);
      this.component.exclusionList = ExclusionLists;

      selector.options = ExclusionListSelectorController.getExclusionListSelectorOptions(ExclusionLists);
    } catch (e) {
      console.error(e);
      selector.isError = true;
      selector.options = [];
    } finally {
      selector.isLoading = false;
    }

    return false;
  }

  private async getExclusionLists(searchString: string): Promise<Array<ExclusionListItem>> {
    if (!this.searchStringToExclusionLists.has(searchString)) {
      const companyId = this.dependencies.getActiveCompany().companyId;
      const data = await this.dataRepository.getExclusionList(companyId, ExclusionListTypes.ANTIMALWARE, {includeDescription: true});
      this.searchStringToExclusionLists.set(searchString, data && data.list ? data.list : []);
    }

    return this.searchStringToExclusionLists.get(searchString) || [];
  }

  async getDataForView(): Promise<ExclusionListSelectorWebComponentSchema> {
    const companyId = this.dependencies.getActiveCompany().companyId;
    const response = await this.dataRepository.getExclusionList(companyId, ExclusionListTypes.ANTIMALWARE, {includeDescription: true});
    if (response !== undefined && response['data'] !== undefined) {
      this.component.exclusionList = response['data'];
    }
    return {
      disabled: this.component.disabled,
      selectedTarget: this.component.selectedTarget,
      list: this.component.exclusionList,
    };
  }
}
